import { useTranslation } from 'react-i18next';

import { CheckboxMenu, type CheckboxMenuSection } from '@/components/molecules/CheckboxMenu';
import { joinAuditTexts, useAuditText } from '@/domain/auditLog';

// More names than this read worse than a count on a toggle.
const NAMED_SELECTIONS = 2;

interface FilterMenuProps<TValue extends string> {
    label: string;
    /** One section of options, or several under group headers. */
    options: readonly CheckboxMenuSection<TValue>[];
    selected: readonly TValue[] | undefined;
    onChange: (values: TValue[]) => void;
    searchable?: boolean;
    /** The id of a visible label beside the menu, in a form row. */
    labelledBy?: string;
}

/** A list filter: "Any" when nothing is ticked, else the ticked names or their count. */
export default function FilterMenu<TValue extends string>(props: FilterMenuProps<TValue>) {
    const { label, options, selected, onChange, searchable, labelledBy } = props;
    const { t } = useTranslation();
    const text = useAuditText();
    const chosen: readonly TValue[] = selected ?? [];

    let summary = t('auditLog.filters.selectedCount', { count: chosen.length });
    if (chosen.length === 0) summary = t('auditLog.filters.any');
    if (chosen.length > 0 && chosen.length <= NAMED_SELECTIONS) {
        const labels = new Map(
            options
                .flatMap((section) => section.options)
                .map((option): [string, string] => [option.value, option.label])
        );
        summary = text(joinAuditTexts(chosen.map((value) => labels.get(value) ?? value)));
    }

    return (
        <CheckboxMenu
            label={label}
            summary={summary}
            options={options}
            selected={chosen}
            onChange={onChange}
            searchable={searchable}
            labelledBy={labelledBy}
        />
    );
}
